import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MessageItem, TickerSpeed } from '@wallflower/shared';
import { TICKER_PX_PER_SECOND } from '@wallflower/shared';

interface Props {
  messages: MessageItem[];
  speed: TickerSpeed;
}

/**
 * The message strip along the bottom of the slideshow.
 *
 * The list is repeated enough times to cover the screen plus one whole copy,
 * then translated by exactly one copy's width, forever. That is what makes the
 * wrap seamless at any message count: with two short messages the strip is
 * still wider than the viewport, so nothing ever appears out of thin air
 * mid-screen - the next copy is already on stage when the previous one leaves.
 *
 * The travel itself runs on the Web Animations API rather than a CSS keyframe
 * because we have to change the duration as messages arrive. Re-setting a CSS
 * animation-duration re-maps elapsed time against the new duration and the
 * strip jumps; here we cancel and restart, which begins at phase zero. Restarts
 * are deferred to the moment a loop completes, where phase zero is exactly
 * where the strip already is, so the swap is invisible.
 */
export default function Ticker({ messages, speed }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  const [rendered, setRendered] = useState(messages);
  const [pending, setPending] = useState<MessageItem[] | null>(null);
  const [copyWidth, setCopyWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  // Enough copies that one copy can slide fully off the left while the rest
  // still cover the viewport. Never fewer than two, so the loop always wraps.
  const copies =
    copyWidth > 0 ? Math.max(2, Math.ceil(viewportWidth / copyWidth) + 1) : 2;
  // Constant speed regardless of how many messages are in the loop.
  const durationSeconds = copyWidth > 0 ? copyWidth / TICKER_PX_PER_SECOND[speed] : 0;

  useLayoutEffect(() => {
    const copy = copyRef.current;
    const viewport = viewportRef.current;
    if (!copy || !viewport) return;

    const measure = () => {
      setCopyWidth(copy.getBoundingClientRect().width);
      setViewportWidth(viewport.getBoundingClientRect().width);
    };
    measure();

    // Widths move under us when a web font finishes loading or the TV rotates.
    const observer = new ResizeObserver(measure);
    observer.observe(copy);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [rendered, copies]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || copyWidth <= 0 || durationSeconds <= 0) return;

    const animation = track.animate(
      [
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-copyWidth}px, 0, 0)` },
      ],
      { duration: durationSeconds * 1000, iterations: Infinity, easing: 'linear' },
    );
    animationRef.current = animation;
    return () => {
      animation.cancel();
      animationRef.current = null;
    };
  }, [copyWidth, durationSeconds, rendered, copies]);

  // The parent hands us a freshly filtered array on every slide change, so
  // compare what is actually on screen, not array identity - otherwise the
  // strip would keep restarting for a list that never changed.
  const signature = signatureOf(messages);
  const renderedSignature = signatureOf(rendered);

  useEffect(() => {
    if (signature === renderedSignature) {
      setPending(null);
      return;
    }
    setPending(messages);
  }, [messages, signature, renderedSignature]);

  // Hold new messages until the strip is back at phase zero, where a restart
  // costs nothing visually. An empty ticker has nothing to disturb, so it
  // takes them immediately.
  useEffect(() => {
    if (!pending) return;
    const animation = animationRef.current;
    const cycleMs = durationSeconds * 1000;
    if (rendered.length === 0 || !animation || cycleMs <= 0) {
      setRendered(pending);
      setPending(null);
      return;
    }

    const elapsed = Number(animation.currentTime ?? 0);
    const remaining = cycleMs - (elapsed % cycleMs);
    const timer = setTimeout(() => {
      setRendered(pending);
      setPending(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [pending, rendered.length, durationSeconds]);

  if (rendered.length === 0) return null;

  if (reducedMotion) return <FadingTicker messages={rendered} />;

  // z-30 puts the strip above the empty state (z-10) as well as the photo, so
  // the first message of the night shows before anyone has uploaded a picture.
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 overflow-hidden">
      {/* A tall soft scrim rather than a solid bar: the photo still runs to the
          edge, but the text stays readable over a bright one. */}
      <div
        className="absolute inset-x-0 bottom-0 h-[13vh]"
        style={{
          background:
            'linear-gradient(to top, rgba(10,7,15,0.92) 0%, rgba(10,7,15,0.78) 45%, rgba(10,7,15,0) 100%)',
        }}
        aria-hidden
      />
      <div ref={viewportRef} className="relative flex h-[7vh] items-center overflow-hidden">
        <div
          ref={trackRef}
          data-ticker-track
          className="flex w-max shrink-0 items-center whitespace-nowrap"
          style={{ willChange: 'transform' }}
        >
          {/* Identical copies; the animation travels exactly one of them. */}
          {Array.from({ length: copies }, (_, copy) => (
            <div
              key={copy}
              ref={copy === 0 ? copyRef : undefined}
              className="flex shrink-0 items-center"
              aria-hidden={copy > 0}
            >
              {rendered.map((message) => (
                <Entry key={`${copy}-${message.id}`} message={message} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function signatureOf(messages: MessageItem[]): string {
  return messages.map((m) => `${m.id}:${m.body}:${m.authorName ?? ''}:${m.fromHost}`).join('|');
}

function Entry({ message }: { message: MessageItem }) {
  return (
    <span className="flex items-center text-[1.5vw] leading-none">
      <span
        className="mr-[0.6vw]"
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          color: message.fromHost ? 'var(--color-sulphur)' : 'var(--color-petal)',
        }}
      >
        {message.fromHost ? (message.authorName ?? 'Your hosts') : (message.authorName ?? 'Someone')}
      </span>
      <span className="text-chalk/95">{message.body}</span>
      <span className="mx-[1.6vw] text-[1vw] text-muted" aria-hidden>
        ◆
      </span>
    </span>
  );
}

/** Reduced motion: no travel at all, one message at a time on a timer. */
function FadingTicker({ messages }: { messages: MessageItem[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % messages.length), 7000);
    return () => clearInterval(timer);
  }, [messages.length]);

  const message = messages[index % messages.length];
  if (!message) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30">
      <div
        className="absolute inset-x-0 bottom-0 h-[13vh]"
        style={{
          background:
            'linear-gradient(to top, rgba(10,7,15,0.92) 0%, rgba(10,7,15,0.78) 45%, rgba(10,7,15,0) 100%)',
        }}
        aria-hidden
      />
      <div className="relative flex h-[7vh] items-center justify-center px-[4vw]">
        <span className="truncate text-[1.5vw] leading-none">
          <span
            className="mr-[0.6vw]"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              color: message.fromHost ? 'var(--color-sulphur)' : 'var(--color-petal)',
            }}
          >
            {message.fromHost
              ? (message.authorName ?? 'Your hosts')
              : (message.authorName ?? 'Someone')}
          </span>
          <span className="text-chalk/95">{message.body}</span>
        </span>
      </div>
    </div>
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
